import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Download, Copy } from 'lucide-react';
import Button from '../Button';

interface PieChartData {
  option: string;
  count: number;
  percentage: number;
}

interface BrandedPieChartProps {
  data: PieChartData[];
  title?: string;
  className?: string;
}

const BrandedPieChart: React.FC<BrandedPieChartProps> = ({ 
  data, 
  title,
  className = '' 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  // Safety check for empty or undefined data
  if (!data || data.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="text-center py-4 text-gray-500">
          No data available for pie chart
        </div>
      </div>
    );
  }

  // Brand colors for pie chart with corresponding text colors
  const brandColors = [
    '#07E5D1', // Primary Teal
    '#6B7280', // Gray for contrast
    '#10B981', // Success Green
    '#3B82F6', // Info Blue
  ];

  const textColors = [
    '#1C1C1E', // Dark text for light teal
    '#FFFFFF', // White text for dark gray
    '#FFFFFF', // White text for green
    '#FFFFFF', // White text for blue
  ];

  // Calculate total for percentages
  const total = data.reduce((sum, item) => sum + item.count, 0);

  const handleCopy = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff' });
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
          ]);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }
      });
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = 'pie-chart.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  
  // Calculate angles for pie slices - start from top (12 o'clock) instead of using CSS rotation
  let currentAngle = -90; // Start from top instead of right
  const slices = data.map((item, index) => {
    const percentage = (item.count / total) * 100;
    const angle = (item.count / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    // Calculate path for SVG arc
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    const radius = 80;
    const centerX = 100;
    const centerY = 100;
    
    const x1 = centerX + radius * Math.cos(startAngleRad);
    const y1 = centerY + radius * Math.sin(startAngleRad);
    const x2 = centerX + radius * Math.cos(endAngleRad);
    const y2 = centerY + radius * Math.sin(endAngleRad);

    // Calculate label position (at 60% of radius for better placement)
    const midAngle = (startAngle + endAngle) / 2;
    const midAngleRad = (midAngle * Math.PI) / 180;
    const labelRadius = radius * 0.6;
    const labelX = centerX + labelRadius * Math.cos(midAngleRad);
    const labelY = centerY + labelRadius * Math.sin(midAngleRad);
    
    // Handle edge case: if angle is 360 degrees (100% of one option)
    let pathData;
    if (angle >= 359.9) {
      // Draw a full circle using two semicircles for 100% case
      const topX = centerX;
      const topY = centerY - radius;
      const bottomX = centerX;
      const bottomY = centerY + radius;
      
      pathData = [
        `M ${centerX} ${centerY}`,
        `L ${topX} ${topY}`,
        `A ${radius} ${radius} 0 0 1 ${bottomX} ${bottomY}`,
        `A ${radius} ${radius} 0 0 1 ${topX} ${topY}`,
        'Z'
      ].join(' ');
    } else {
      const largeArcFlag = angle > 180 ? 1 : 0;
      pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');
    }

    return {
      ...item,
      percentage,
      color: brandColors[index % brandColors.length],
      textColor: textColors[index % textColors.length],
      pathData,
      startAngle,
      endAngle,
      labelX,
      labelY
    };
  });

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        {title && (
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-[#07E5D1] rounded-full"></div>
            <h4 className="text-sm font-medium text-gray-900">{title}</h4>
          </div>
        )}
        <div className="flex gap-2 no-print">
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Download className="h-4 w-4" />}
              onClick={handleDownload}
              title="Download chart as image"
              onMouseEnter={() => setShowTooltip('download')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'download' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Download chart as image
              </div>
            )}
          </div>
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Copy className="h-4 w-4" />}
              onClick={handleCopy}
              title="Copy chart as image"
              onMouseEnter={() => setShowTooltip('copy')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'copy' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Copy chart as image
              </div>
            )}
          </div>
          {copied && (
            <span className="text-green-600 text-xs ml-1">Copied!</span>
          )}
        </div>
      </div>
      
      <div ref={chartRef} className="w-full h-full">
        <div className="flex items-center justify-center space-x-8">
          {/* Pie Chart SVG */}
          <div className="relative">
          <svg width="200" height="200" viewBox="0 0 200 200">
            {slices.map((slice, index) => (
              <g key={index}>
                <path
                  d={slice.pathData}
                  fill={slice.color}
                  stroke="white"
                  strokeWidth="2"
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                />
                {/* Percentage label on slice - only show if slice is large enough */}
                {slice.percentage > 3 && (
                  <text
                    x={slice.labelX}
                    y={slice.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ 
                      fontSize: '12px', 
                      fontWeight: 600,
                      fill: slice.textColor
                    }}
                  >
                    {slice.percentage.toFixed(1)}%
                  </text>
                )}
              </g>
            ))}
          </svg>
        </div>

        {/* Legend */}
        <div className="space-y-3">
          {slices.map((slice, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: slice.color }}
              ></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 capitalize">
                  {slice.option}
                </div>
                <div className="text-xs text-gray-500">
                  {slice.count} responses ({slice.percentage.toFixed(1)}%)
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default BrandedPieChart;